/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.loading = this.querySelector('.js-search-loading');
      this.popular = this.querySelector('.js-search-popular');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.requestController = null;
      this.requestSequence = 0;
      this.liveRegionTimeout = null;
      this.dismissed = false;
      this.focusHandler = this.handleFocus.bind(this);
      this.immediateInputHandler = this.handleImmediateInput.bind(this);
      this.inputHandler = debounce(this.handleInput.bind(this), 200);
      this.productTypeChangeHandler = this.handleProductTypeChange.bind(this);
      this.submitHandler = this.handleSubmit.bind(this);
      this.keydownHandler = this.handleKeydown.bind(this);
      this.resetHandler = this.handleReset.bind(this);
      this.outsidePointerHandler = this.handleOutsidePointer.bind(this);
      this.outsideFocusHandler = this.handleOutsideFocus.bind(this);
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
      this.input.removeEventListener('focus', this.focusHandler);
      this.input.removeEventListener('click', this.focusHandler);
      this.input.removeEventListener('input', this.immediateInputHandler);
      this.input.removeEventListener('input', this.inputHandler);
      this.querySelector('.search').removeEventListener('submit', this.submitHandler);
      this.removeEventListener('keydown', this.keydownHandler);
      this.resetBtn.removeEventListener('click', this.resetHandler);

      if (this.productTypeSelect) {
        this.productTypeSelect.removeEventListener('change', this.productTypeChangeHandler);
      }

      window.clearTimeout(this.liveRegionTimeout);
    }

    addListeners() {
      this.input.addEventListener('focus', this.focusHandler);
      this.input.addEventListener('click', this.focusHandler);
      this.input.addEventListener('input', this.immediateInputHandler);
      this.input.addEventListener('input', this.inputHandler);
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.productTypeChangeHandler);
      }
      this.querySelector('.search').addEventListener('submit', this.submitHandler);
      this.addEventListener('keydown', this.keydownHandler);
      this.resetBtn.addEventListener('click', this.resetHandler);
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (!this.getQuery().length) evt.preventDefault();
    }

    /**
     * Replaces stale suggestions with an immediate, stable loading placeholder.
     */
    handleImmediateInput() {
      this.dismissed = false;
      if (this.requestController) this.cancelPendingRequest();

      const searchTerm = this.getQuery();
      if (!searchTerm.length) {
        this.openPopular();
        return;
      }

      const renderedResults = this.results.querySelector('[data-search-query]');
      if (!renderedResults || renderedResults.dataset.searchQuery !== searchTerm) {
        this.showLoading();
      }

      this.resetBtn.hidden = false;
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      if (this.dismissed) return;

      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.openPopular();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      this.dismissed = false;
      const searchTerm = this.getQuery();
      if (!searchTerm.length) {
        this.openPopular();
        return;
      }

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          if (this.hasAttribute('open')) {
            evt.preventDefault();
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          if (this.hasAttribute('open') && this.selectOption()) evt.preventDefault();
          break;

        case 'Escape':
          if (this.hasAttribute('open')) {
            evt.preventDefault();
            this.close();
          }
          break;

        // no default
      }
    }

    /**
     * Closes the panel when a pointer lands outside this search component.
     * @param {PointerEvent} evt - Pointer event.
     */
    handleOutsidePointer(evt) {
      const eventPath = typeof evt.composedPath === 'function' ? evt.composedPath() : [];
      if (!eventPath.includes(this) && !this.contains(evt.target)) this.close();
    }

    /**
     * Closes the panel when keyboard focus leaves this search component.
     * @param {FocusEvent} evt - Focus event.
     */
    handleOutsideFocus(evt) {
      if (!this.contains(evt.target)) this.close();
    }

    /**
     * Clears the current query and returns to the popular-search shortcuts.
     * @param {MouseEvent} evt - Reset button event.
     */
    handleReset(evt) {
      evt.preventDefault();
      this.cancelPendingRequest();
      this.input.value = '';
      this.input.setAttribute('value', '');
      this.results.innerHTML = '';
      this.removeAttribute('results');
      this.input.focus();
      this.openPopular();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      if (this.getAttribute('results') === 'loading') return;

      const activePanel = this.getAttribute('results') === 'popular' ? this.popular : this.results;
      const allResults = Array.from(activePanel.querySelectorAll('.predictive-search__item'))
        .filter((item) => item.getClientRects().length > 0);
      if (!allResults.length) return;

      const selectedResult = activePanel.querySelector('.predictive-search__item[aria-selected="true"]');
      const selectedIndex = allResults.indexOf(selectedResult);
      const direction = key === 'ArrowUp' ? -1 : 1;
      const initialIndex = direction === -1 ? allResults.length - 1 : 0;
      const nextIndex = selectedIndex === -1
        ? initialIndex
        : (selectedIndex + direction + allResults.length) % allResults.length;
      const resultToSelect = allResults[nextIndex];

      this.statusEl.textContent = '';

      if (resultToSelect) {
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent.trim());
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
        resultToSelect.scrollIntoView({ block: 'nearest' });
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedOption = this.querySelector('.predictive-search__item[aria-selected="true"]');
      const selectedResult = selectedOption && selectedOption.querySelector('.js-search-link');
      if (!selectedResult) return false;

      selectedResult.click();
      return true;
    }

    /**
     * Cancels any request whose response no longer belongs to the visible query.
     */
    cancelPendingRequest() {
      if (this.requestController) this.requestController.abort();
      this.requestController = null;
      this.requestSequence += 1;
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      this.cancelPendingRequest();
      const requestSequence = this.requestSequence;
      this.setLiveRegionLoadingState();
      if (this.popular) this.popular.hidden = true;

      const searchTypes = [];
      if (theme.settings.pSearchShowSuggestions && !(this.productTypeInput && this.productTypeInput.value)) {
        searchTypes.push('query');
      }
      if (theme.settings.pSearchShowProducts) searchTypes.push('product');
      if (theme.settings.pSearchShowCollections) searchTypes.push('collection');

      if (!searchTypes.length) {
        this.close();
        return;
      }

      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      const searchParams = new URLSearchParams();
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams.set('q', `product_type:${this.productTypeInput.value} AND ${searchTerm}`);
      } else {
        searchParams.set('q', searchTerm);
      }

      searchParams.set('resources[type]', searchTypes.join(','));
      searchParams.set('resources[limit]', theme.settings.pSearchLimit || 10);
      searchParams.set('resources[limit_scope]', theme.settings.pSearchLimitScope || 'each');
      searchParams.set('resources[options][fields]', searchFields);
      searchParams.set('section_id', 'predictive-search');

      const queryKey = searchParams.toString();
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey], searchTerm);
        return;
      }

      this.showLoading();

      const requestController = new AbortController();
      this.requestController = requestController;

      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams.toString()}`, {
          signal: requestController.signal
        });
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        if (!resultsEl || requestSequence !== this.requestSequence || searchTerm !== this.getQuery()) return;

        await this.addCategoryFallbackProducts(resultsEl, searchParams, requestController.signal);
        if (requestSequence !== this.requestSequence || searchTerm !== this.getQuery()) return;

        const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup, searchTerm);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.setLiveRegionText('Rezultatele nu au putut fi încărcate. Apasă Enter pentru a căuta.');
        this.close();
      } finally {
        if (this.requestController === requestController) this.requestController = null;
      }
    }

    /**
     * Expands a matching category when Shopify returns no products for a short prefix.
     * @param {Element} resultsEl - Original predictive-search section.
     * @param {URLSearchParams} searchParams - Original request parameters.
     * @param {AbortSignal} signal - Shared request cancellation signal.
     */
    async addCategoryFallbackProducts(resultsEl, searchParams, signal) {
      const resultsRoot = resultsEl.querySelector('[data-result-count]');
      if (!resultsRoot || Number(resultsRoot.dataset.resultCount) > 0 || !Number(resultsRoot.dataset.categoryCount)) {
        return;
      }

      const categoryLink = resultsRoot.querySelector('.predictive-search__category-link');
      const fallbackQuery = categoryLink && categoryLink.textContent.trim();
      const searchTerm = searchParams.get('q') || '';
      if (!fallbackQuery || fallbackQuery.toLocaleLowerCase().indexOf(searchTerm.toLocaleLowerCase()) !== 0) {
        return;
      }

      const fallbackParams = new URLSearchParams(searchParams);
      fallbackParams.set('q', fallbackQuery);

      const response = await fetch(`${theme.routes.predictiveSearch}?${fallbackParams.toString()}`, { signal });
      if (!response.ok) return;

      const fallbackTemplate = document.createElement('template');
      fallbackTemplate.innerHTML = await response.text();

      const fallbackRoot = fallbackTemplate.content.querySelector('[data-result-count]');
      const fallbackProduct = fallbackRoot && fallbackRoot.querySelector('[data-predictive-product]');
      const fallbackGroup = fallbackProduct && fallbackProduct.closest('.predictive-search__group');
      const emptyState = resultsRoot.querySelector('.predictive-search__no-products');
      if (!fallbackRoot || !fallbackGroup || !emptyState) return;

      emptyState.replaceWith(fallbackGroup.cloneNode(true));
      resultsRoot.dataset.resultCount = fallbackRoot.dataset.resultCount;
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      window.clearTimeout(this.liveRegionTimeout);
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      this.liveRegionTimeout = window.setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1200);
    }

    /**
     * Keeps the suggestions panel open while fresh results are loading.
     */
    showLoading() {
      if (!this.loading || this.dismissed || !this.getQuery().length) return;

      this.results.hidden = true;
      if (this.popular) this.popular.hidden = true;
      this.loading.hidden = false;
      this.results.querySelectorAll('[aria-selected="true"]').forEach((item) => {
        item.setAttribute('aria-selected', 'false');
      });
      this.input.removeAttribute('aria-activedescendant');
      this.input.setAttribute('aria-busy', 'true');
      this.setAttribute('results', 'loading');
      this.open();
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     * @param {string} searchTerm - Query associated with the rendered response.
     */
    renderResults(resultsMarkup, searchTerm) {
      if (searchTerm !== this.getQuery()) return;

      if (this.popular) this.popular.hidden = true;
      if (this.loading) this.loading.hidden = true;
      this.results.hidden = false;
      this.results.innerHTML = resultsMarkup;
      this.results.querySelectorAll('.predictive-search__item').forEach((item, index) => {
        item.id = `${this.input.id}-predictive-search-option-${index}`;
        item.setAttribute('aria-selected', 'false');
      });

      const resultsRoot = this.results.querySelector('[data-result-count]');
      const totalResultCount = Number(resultsRoot && resultsRoot.dataset.resultCount) || 0;
      const productLimit = window.matchMedia('(max-width: 768.98px)').matches ? 3 : 6;
      const resultCount = Math.min(totalResultCount, productLimit);
      const categoryCount = Number(resultsRoot && resultsRoot.dataset.categoryCount) || 0;
      const productsLabel = resultCount === 1 ? '1 produs găsit' : `${resultCount} produse găsite`;
      const categoriesLabel = categoryCount === 1 ? '1 categorie' : `${categoryCount} categorii`;
      this.setLiveRegionText(categoryCount ? `${productsLabel}, ${categoriesLabel}` : productsLabel);
      this.dispatchEvent(new CustomEvent('adore:predictive-rendered', {
        bubbles: true,
        detail: { query: searchTerm, resultCount }
      }));

      this.setAttribute('results', true);
      this.input.removeAttribute('aria-busy');
      this.input.setAttribute('aria-controls', `${this.input.id}-predictive-search-results`);
      this.open();
    }

    /**
     * Opens the curated category shortcuts when the query is empty.
     */
    openPopular() {
      if (!this.popular) {
        this.close();
        return;
      }

      this.cancelPendingRequest();
      this.results.hidden = true;
      if (this.loading) this.loading.hidden = true;
      this.popular.hidden = false;
      this.popular.querySelectorAll('[aria-selected="true"]').forEach((item) => {
        item.setAttribute('aria-selected', 'false');
      });
      this.input.removeAttribute('aria-activedescendant');
      this.input.removeAttribute('aria-busy');
      this.input.setAttribute('aria-controls', `${this.input.id}-predictive-search-popular-results`);
      this.setAttribute('results', 'popular');
      this.open();
    }

    /**
     * Opens the current panel without dimming or locking the page.
     */
    open() {
      this.dismissed = false;
      this.overlay.classList.remove('is-visible');
      this.resetBtn.hidden = !this.getQuery().length;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');

      document.addEventListener('pointerdown', this.outsidePointerHandler, true);
      document.addEventListener('focusin', this.outsideFocusHandler);
    }

    /**
     * Closes the results listbox.
     */
    close() {
      this.dismissed = true;
      this.cancelPendingRequest();
      this.querySelectorAll('.predictive-search__item[aria-selected="true"]').forEach((item) => {
        item.setAttribute('aria-selected', 'false');
      });

      this.removeAttribute('open');
      if (this.loading) this.loading.hidden = true;
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.removeAttribute('aria-activedescendant');
      this.input.removeAttribute('aria-busy');
      this.input.setAttribute('aria-expanded', 'false');

      document.removeEventListener('pointerdown', this.outsidePointerHandler, true);
      document.removeEventListener('focusin', this.outsideFocusHandler);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
